// Copyright (c) Microsoft. All rights reserved.

import { Button } from '@fluentui/react-button';
import { makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle } from '@fluentui/react-dialog';
import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up the worker for PDF.js using local worker file
// pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//     'pdfjs-dist/build/pdf.worker.min.mjs',
//     import.meta.url,
// ).toString();
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const useClasses = makeStyles({
    root: {
        width: '95vw',
        maxWidth: '95vw',
        height: '90vh',
        maxHeight: '90vh',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap(tokens.spacingVerticalM),
        maxHeight: 'calc(90vh - 120px)', // Reserve space for title and actions
        minHeight: '400px',
    },
    pdfContainer: {
        flex: 1,
        ...shorthands.overflow('auto'),
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        backgroundColor: tokens.colorNeutralBackground3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    },
    page: {
        marginBottom: tokens.spacingVerticalM,
    },
    actions: {
        paddingTop: tokens.spacingVerticalL,
    },
    loadingText: {
        color: tokens.colorNeutralForeground2,
        textAlign: 'center',
        padding: tokens.spacingVerticalXL,
    },
    errorText: {
        color: tokens.colorPaletteRedForeground1,
        textAlign: 'center',
        padding: tokens.spacingVerticalXL,
    },
});

// Container padding value used for responsive width calculation
const PDF_CONTAINER_PADDING = 24; // Horizontal padding for PDF container

interface PolicyAgreementDialogProps {
    onAgree: () => void;
}

export const PolicyAgreementDialog: React.FC<PolicyAgreementDialogProps> = ({ onAgree }) => {
    const classes = useClasses();
    const [numPages, setNumPages] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [containerWidth, setContainerWidth] = useState<number>(1000);
    const containerRef = useRef<HTMLDivElement>(null);

    // Update container width when the component mounts or window resizes
    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                // Set width to container width minus padding - no maximum cap for better readability
                const width = containerRef.current.clientWidth - PDF_CONTAINER_PADDING;
                setContainerWidth(width);
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => {
            window.removeEventListener('resize', updateWidth);
        };
    }, []);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        setLoading(false);
    };

    const onDocumentLoadError = (error: Error) => {
        console.error('Error loading PDF:', error);
        setError(
            'Unable to load the policy document. Please check your network connection and try refreshing the page. If the problem persists, contact support.',
        );
        setLoading(false);
    };

    return (
        <Dialog open={true} modalType="alert">
            <DialogSurface className={classes.root}>
                <DialogBody>
                    <DialogTitle>GenGPT Terms of Use</DialogTitle>
                    <DialogContent className={classes.content}>
                        <div className={classes.pdfContainer} ref={containerRef}>
                            {loading && <div className={classes.loadingText}>Loading policy document...</div>}
                            {error && <div className={classes.errorText}>{error}</div>}
                            <Document
                                file="/policies/terms-of-use.pdf"
                                onLoadSuccess={onDocumentLoadSuccess}
                                onLoadError={onDocumentLoadError}
                                loading=""
                            >
                                {Array.from(new Array(numPages), (_, index) => (
                                    <Page
                                        key={`page_${index + 1}`}
                                        pageNumber={index + 1}
                                        className={classes.page}
                                        renderTextLayer={true}
                                        renderAnnotationLayer={true}
                                        width={containerWidth}
                                    />
                                ))}
                            </Document>
                        </div>
                    </DialogContent>
                    <DialogActions className={classes.actions}>
                        <Button appearance="primary" onClick={onAgree} disabled={loading || error !== null}>
                            I Agree
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
